import React, { useContext, useEffect, useState } from "react";
import { Button, Card, Table } from "antd";
import { PageHeader } from "@ant-design/pro-layout";
import { HomeOutlined, InboxOutlined, EyeOutlined } from "@ant-design/icons";
import CustomBreadcrumb from "../../../../components/CustomBreadcrumb";
import { useTranslation } from "react-i18next";
import { getStock } from "../../../../network/network";
import { AppContext } from "../../../../context/AppContextProvider";
import CurrencyFormatter from "../../../../components/CurrencyFormatter";
import StockOverviewModal from "./StockOverviewModal";
import { handleApiError } from "../../../../utils/errorHandler";

const Stock = () => {
  const { appConfig } = useContext(AppContext);
  const [isDataLoading, setIsDataLoading] = useState(false);
  const [stockData, setStockData] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [modalVisible, setModalVisible] = useState(false);
  const { t } = useTranslation();

  const [paginationData, setPaginationData] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [perPageSize, setPerPageSize] = useState(10);

  useEffect(() => {
    setIsDataLoading(true);

    getStock({
      page: currentPage,
      per_page: perPageSize,
    })
      .then((response) => {
        if (response?.data?.success === false) {
          handleApiError(response, t);
          return;
        }

        const data = response.data?.data || {};
        const stock = data.Stock || [];
        const pagination = data.pagination || {};

        setStockData(stock);
        setPaginationData(pagination);
      })
      .catch((error) => handleApiError(error, t))
      .finally(() => {
        setIsDataLoading(false);
      });
  }, [currentPage, perPageSize, t]);

  const breadcrumbRoutes = [
    {
      path: "/",
      breadcrumbName: t("home"),
      icon: <HomeOutlined />,
    },
    {
      path: "inventory",
      breadcrumbName: t("inventory"),
      icon: <InboxOutlined />,
    },
  ];

  const handleOpenModal = (row) => {
    // row is a stock row, we only pass its product to the modal
    if (row.product) {
      setSelectedProduct(row.product);
      setModalVisible(true);
    }
  };

  const handleCloseModal = () => {
    setModalVisible(false);
    setSelectedProduct(null);
  };

  const handleTableChange = (pagination) => {
    setPaginationData(pagination);
    setCurrentPage(pagination.current);
    setPerPageSize(pagination.pageSize);
  };

  const tableColumns = [
    {
      title: t("image"),
      width: "85px",
      render: (record) => {
        return (
          record.product && (
            <img
              alt={record.product.name_en}
              src={
                record.product.image
                  ? record.product.image
                  : "/images/image-not-found.png"
              }
              className="app-table-thumbnail"
            />
          )
        );
      },
    },
    {
      title: t("product"),
      render: (record) => {
        return (
          record.product &&
          (appConfig.direction === "ltr"
            ? record.product.name_en
            : record.product.name)
        );
      },
    },
    {
      title: t("total_stock"),
      align: "center",
      dataIndex: "total_branches_stock",
      render: (total_branches_stock) => {
        return (
          <div
            style={{
              color:
                Math.sign(total_branches_stock) === 1
                  ? "var(--color-success)"
                  : "var(--color-danger)",
            }}
          >
            {total_branches_stock}
          </div>
        );
      },
    },
    {
      title: t("branch_stock"),
      align: "center",
      dataIndex: "quantity",
      render: (quantity) => {
        return (
          <div
            style={{
              color:
                Math.sign(quantity) === 1
                  ? "var(--color-success)"
                  : "var(--color-danger)",
            }}
          >
            {quantity}
          </div>
        );
      },
    },
    {
      title: t("price"),
      render: (record) => {
        return (
          record.product && <CurrencyFormatter value={record.product.price} />
        );
      },
    },
    {
      title: t("category"),
      render: (record) => {
        return (
          record.product &&
          (appConfig.direction === "ltr"
            ? record.product.category
            : record.product.category_ar)
        );
      },
    },
    {
      title: t("action"),
      align: "center",
      width: 85,
      render: (record) => (
        <Button type="link" onClick={() => handleOpenModal(record)}>
          <EyeOutlined title={t("view_details")} />
        </Button>
      ),
    },
  ];

  return (
    <>
      <Card className="app-mb-20">
        <CustomBreadcrumb menus={breadcrumbRoutes} />
        <PageHeader className="app-p-0" title={t("stock")} />
      </Card>

      <Card>
        <Table
          columns={tableColumns}
          dataSource={stockData.map((item, index) => ({
            ...item,
            key: item.id || index,
          }))}
          loading={isDataLoading}
          pagination={
            paginationData.total < 10
              ? false
              : {
                  current: paginationData.current_page || currentPage,
                  pageSize: paginationData.per_page || perPageSize,
                  total: paginationData.total || 0,
                  showSizeChanger: true,
                }
          }
          onChange={handleTableChange}
        />
      </Card>

      <StockOverviewModal
        visible={modalVisible}
        product={selectedProduct}
        onClose={handleCloseModal}
      />
    </>
  );
};

export default Stock;
